
import dayjs from "dayjs";
import { Component, mixins, namespace } from "nuxt-property-decorator";
import GridContainer from "~/components/atoms/sectioning/GridContainer.vue";
import EsyFigcaption from "~/components/atoms/text/EsyFigcaption.vue";
import Accordion from "~/components/molecules/display/Accordion.vue";
import CollapsibleSection from "~/components/molecules/display/CollapsibleSection.vue";
import PelletsArticle from "~/components/organisms/display/pellets/PelletsArticle.vue";
import PelletsCurrentPriceSection from "~/components/organisms/display/pellets/PelletsCurrentPriceSection.vue";
import PelletsDealerCarousel from "~/components/organisms/display/pellets/PelletsDealerCarousel.vue";
import PelletsGuideCarousel from "~/components/organisms/display/pellets/PelletsGuideCarousel.vue";
import PelletsHeroSection from "~/components/organisms/display/pellets/PelletsHeroSection.vue";
import PelletsHowItWorks from "~/components/organisms/display/pellets/PelletsHowItWorks.vue";
import PelletsStickyComparisonBar from "~/components/organisms/display/pellets/PelletsStickyComparisonBar.vue";
import PelletsTableSection from "~/components/organisms/display/pellets/PelletsTableSection.vue";
import PelletsTrustBadges from "~/components/organisms/display/pellets/PelletsTrustBadges.vue";
import { pelletsArticles } from "~/defaults/pellets/pelletsArticles";
import { pelletsPricesFaq } from "~/defaults/pellets/pelletsPricesFaq";
import { returnPriceFormatDE } from "~/mixins/formatting";
import GeoPricePageMixin from "~/mixins/geoPricePageMixin";
import PelletsChartRangeMixin from "~/mixins/PelletsChartRange";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import type { PelletsDisplay } from "~/types/Pellets";
import { STANDARD_PELLETS_QUANTITY } from "~/types/Pellets";
import type { PelletsChartMonths } from "~/utils/pelletsChart";
import { buildPelletsDisplay } from "~/utils/pelletsDisplay";

const OilpriceModule = namespace("oilprice");
const PartnerModule = namespace("partner");

const HERO_BANNER_1920 = require("~/assets/images/pellets/pellets-banner.webp");
const HERO_BANNER_720 = require("~/assets/images/pellets/pellets-banner-720w.webp");
const HERO_BANNER_960 = require("~/assets/images/pellets/pellets-banner-960w.webp");
const HERO_BANNER_1280 = require("~/assets/images/pellets/pellets-banner-1280w.webp");

@Component({
  components: {
    Accordion,
    CollapsibleSection,
    PelletsArticle,
    PelletsCurrentPriceSection,
    PelletsDealerCarousel,
    PelletsGuideCarousel,
    PelletsHeroSection,
    PelletsHowItWorks,
    PelletsStickyComparisonBar,
    PelletsTableSection,
    PelletsTrustBadges,
  },
  methods: {
    returnPriceFormatDE,
  },
})
export default class PelletsLandingPage extends mixins(
  GeoPricePageMixin,
  PelletsChartRangeMixin,
) {
  head() {
    return {
      ...generateMetaTags(this.$route, "pellets"),
      link: [
        {
          rel: "preload",
          as: "image",
          href: HERO_BANNER_1920,
          imagesrcset: `${HERO_BANNER_720} 720w, ${HERO_BANNER_960} 960w, ${HERO_BANNER_1280} 1280w, ${HERO_BANNER_1920} 1920w`,
          imagesizes: "100vw",
          fetchpriority: "high",
        },
        { rel: "canonical", href: this.$route.path },
      ],
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: this.QuestionsList.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          },
        },
      ],
    };
  }

  @OilpriceModule.State("pelletsDistrictPrices")
  pelletsDistrictPrices: any;

  @OilpriceModule.State("pelletsStateAssetsPrice")
  pelletsStateAssetsPrice: any;

  @OilpriceModule.State("pelletsHistoryChartData")
  pelletsHistoryChartData: any;

  @OilpriceModule.State("averageCityPelletPrices")
  averageCityPelletPrices: any;

  @PartnerModule.State("partnerList") partnerList!: any[];
  @PartnerModule.Action("fetchPartnerListItems") fetchPartnerListItems;

  isLoading = true;
  isCtaVisible = true;
  STANDARD_PELLETS_QUANTITY = STANDARD_PELLETS_QUANTITY;

  articles = pelletsArticles;
  pelletsDealers: any[] = [];

  onCtaVisibilityChange(visible: boolean) {
    this.isCtaVisible = visible;
  }

  get pelletsDisplay(): PelletsDisplay {
    return buildPelletsDisplay(
      this.pelletsStateAssetsPrice,
      this.pelletsHistoryChartData,
    );
  }

  get cityTableHeaderData(): string[] {
    const dates = this.averageCityPelletPrices.dates;
    if (dates.actual && dates.previous) {
      return this.generateTableHeaderData("Stadt", [
        dayjs(dates.actual).unix(),
        dayjs(dates.previous).unix(),
      ]);
    }
    return this.generateTableHeaderData("Stadt");
  }

  get stateTableHeaderData(): string[] {
    return this.returnStateTableHeaderData(this.pelletsDistrictPrices.loose);
  }

  get topLooseStatesRows() {
    return this.formatTopStatesTableData(
      this.pelletsDistrictPrices.loose,
      "pelletspreise",
    );
  }

  get topPalletStatesRows() {
    return this.formatTopStatesTableData(
      this.pelletsDistrictPrices.pallet,
      "pelletspreise",
    );
  }

  get looseCitiesRows() {
    return this.formatBigCitiesTableData(
      this.averageCityPelletPrices.loose,
      "pelletspreise",
    );
  }

  get palletCitiesRows() {
    return this.formatBigCitiesTableData(
      this.averageCityPelletPrices.pallet,
      "pelletspreise",
    );
  }

  async fetch() {
    try {
      this.isLoading = true;

      if (process.server) {
        await Promise.all([
          this.fetchPelletsPriceTrend(),
          this.fetchDistrictPriceTrends(),
          this.fetchCityPriceTrends(),
          this.fetchPelletsHistoryPrices(),
          this.fetchPelletsDealers(),
        ]);
      } else {
        await Promise.all([
          this.fetchPelletsPriceTrend(),
          this.fetchPelletsHistoryPrices(),
        ]);

        Promise.all([
          this.fetchDistrictPriceTrends(),
          this.fetchCityPriceTrends(),
          this.fetchPelletsDealers(),
        ]).catch((error) => {
          console.error("Error fetching deferred price data:", error);
        });
      }
    } catch (error) {
      console.error("Error fetching price data:", error);
    } finally {
      this.isLoading = false;
    }
  }

  async fetchPelletsPriceTrend() {
    await this.$store.dispatch("oilprice/fetchPelletsPriceTrend");
  }

  async fetchPelletsHistoryPrices() {
    await this.$store.dispatch("oilprice/fetchPelletsHistoryPrices", {
      monthsQuantity: this.monthsQuantity,
    });
  }

  async dispatchChartFetch(months: PelletsChartMonths): Promise<void> {
    await this.$store.dispatch("oilprice/fetchPelletsHistoryPrices", {
      monthsQuantity: months,
    });
  }

  async fetchDistrictPriceTrends() {
    await this.$store.dispatch("oilprice/fetchPelletsDistrictPriceTrend");
  }

  async fetchCityPriceTrends() {
    await this.$store.dispatch("oilprice/fetchAveragePelletCityPrices");
  }

  async fetchPelletsDealers() {
    await this.fetchPartnerListItems({
      searchValue: "",
      partnerType: "PARTNER_PELLET",
    });
    this.pelletsDealers = [...this.partnerList]
      .sort((a, b) => a._id.localeCompare(b._id))
      .slice(0, 10);
  }

  get loosePriceOneWeekAgo(): number | null {
    if (!this.pelletsHistoryChartData?.loose?.data?.length) {
      return null;
    }
    return this.getPriceFromDaysAgo(this.pelletsHistoryChartData.loose.data, 7);
  }

  get palletPriceOneWeekAgo(): number | null {
    if (!this.pelletsHistoryChartData?.pallet?.data?.length) {
      return null;
    }
    return this.getPriceFromDaysAgo(
      this.pelletsHistoryChartData.pallet.data,
      7,
    );
  }

  get looseDisplayText(): string {
    if (!this.pelletsStateAssetsPrice?.loose?.currentPrice?.value) {
      return "";
    }
    return this.formatPriceComparisonText(
      this.pelletsStateAssetsPrice.loose.currentPrice.value,
      this.loosePriceOneWeekAgo,
    );
  }

  get palletDisplayText(): string {
    if (!this.pelletsStateAssetsPrice?.pallet?.currentPrice?.value) {
      return "";
    }
    return this.formatPriceComparisonText(
      this.pelletsStateAssetsPrice.pallet.currentPrice.value,
      this.palletPriceOneWeekAgo,
    );
  }

  groupAndSortTop3(prices: any[]) {
    const grouped = prices
      .filter((p) => p.currentPrice?.value > 0)
      .reduce((acc, cur) => {
        const price = cur.currentPrice.value.toFixed(2);
        if (!acc[price]) acc[price] = [];
        acc[price].push(cur.name);
        return acc;
      }, {} as Record<string, string[]>);

    return Object.entries(grouped as Record<string, string[]>)
      .map(([price, names]) => ({
        names: names.join(", "),
        price: parseFloat(price),
        unit: "€",
      }))
      .sort((a, b) => a.price - b.price)
      .slice(0, 3);
  }

  get top3PricesLoseWare() {
    if (!this.pelletsDistrictPrices?.loose?.length) return [];
    return this.groupAndSortTop3(this.pelletsDistrictPrices.loose);
  }

  get top3PricesSackWare() {
    if (!this.pelletsDistrictPrices?.pallet?.length) return [];
    return this.groupAndSortTop3(this.pelletsDistrictPrices.pallet);
  }

  get QuestionsList() {
    const loosePrice = this.pelletsStateAssetsPrice?.loose?.currentPrice?.value;
    const formattedPrice = loosePrice ? returnPriceFormatDE(loosePrice) : "";

    return pelletsPricesFaq.map((item) => ({
      ...item,
      answer: item.answer.replace("{{LOOSE_PRICE}}", formattedPrice),
    }));
  }
}
